// Typed API surface for the whole app. Every page/hook talks to
// `api` (exported at the bottom) and nothing else — components
// never import mock data directly. Replacing the mock with a
// real backend later means writing one new class that implements
// `ShardApi` and swapping the export; nothing else changes.

import type { Product } from '@/types/product';
import type { Order, RecipientInfo } from '@/types/order';
import { ALL_PRODUCTS } from '@/constants/products';
import { SEED_ORDERS, SEED_BALANCE } from '@/constants/orders';
import { formatCurrency } from '@/utils/format';
import { generateId } from '@/utils/id';
import { delay } from '@/utils/delay';

export interface CreateOrderInput {
  product: Product;
  recipient: RecipientInfo;
}

export interface ProductsApi {
  getProducts(): Promise<Product[]>;
  getFeaturedProducts(): Promise<Product[]>;
  getPopularProducts(): Promise<Product[]>;
}

export interface OrdersApi {
  getOrders(): Promise<Order[]>;
  createOrder(input: CreateOrderInput): Promise<Order>;
}

export interface WalletApi {
  getBalance(): Promise<number>;
}

export interface ShardApi extends ProductsApi, OrdersApi, WalletApi {}

const NETWORK_LATENCY_MS = 350;

class MockShardApi implements ShardApi {
  private orders: Order[] = [...SEED_ORDERS];
  private balance = SEED_BALANCE;

  async getProducts(): Promise<Product[]> {
    await delay(NETWORK_LATENCY_MS);
    return ALL_PRODUCTS;
  }

  async getFeaturedProducts(): Promise<Product[]> {
    await delay(NETWORK_LATENCY_MS);
    return ALL_PRODUCTS.filter((p) => p.tags?.includes('featured'));
  }

  async getPopularProducts(): Promise<Product[]> {
    await delay(NETWORK_LATENCY_MS);
    return ALL_PRODUCTS.filter((p) => p.tags?.includes('popular'));
  }

  async getOrders(): Promise<Order[]> {
    await delay(NETWORK_LATENCY_MS);
    return [...this.orders];
  }

  async createOrder(input: CreateOrderInput): Promise<Order> {
    await delay(280);
    const order: Order = {
      id: generateId('order'),
      productTitle: input.product.title,
      productCategory: input.product.category,
      recipient: input.recipient,
      amountLabel: formatCurrency(input.product.price, input.product.currency),
      createdAt: new Date().toISOString(),
      status: 'processing',
    };
    this.orders = [order, ...this.orders];
    if (input.product.category === 'stars' && input.recipient.type === 'self') {
      this.balance += input.product.amount;
    }
    return order;
  }

  async getBalance(): Promise<number> {
    await delay(200);
    return this.balance;
  }
}

// Single import point for the whole app. Swap this line to point
// at a real REST/GraphQL client once a backend exists.
export const api: ShardApi = new MockShardApi();
